import React, { useEffect, useState } from "react";
import axios from "../../MyAxios";
import { CircularProgress } from "@mui/material";
import Layout from "../../Layout";
import Title from "../../components/Title";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import useLoggedIn from "../../hooks/useLoggedIn";
import Post from "../../components/Post";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import Comment from "../../components/Comment";
import CommentInput from "@components/CommentInput/CommentInput";
import { GET_COMMENTS, POST_COMMENT, UPLOAD_FILE } from "../../constants/api";
import clsx from "clsx";
import { updatePost } from "@redux/prevPostsSlice";
import { useTheme } from "@emotion/react";
import { Box, Modal, TextField, Typography } from "@mui/material";
import Loader from "../../components/Loader";
import { getLinkMetadata } from "../../redux/postSlice";
import OpenGraphInfo from "@components/OpenGraphInfo";

// ── Extract first URL from any text string ────────────────────────────────────
const extractUrl = (text) => {
  if (!text) return null;
  let t = String(text).trim();
  try {
    t = decodeURIComponent(t);
  } catch (e) { }
  const match =
    t.match(/https?:\/\/[^\s<>'")\]]+/i) || t.match(/www\.[^\s<>'")\]]+/i);
  if (!match) return null;
  let link = match[0].replace(/[.,;:!?')\]]+$/g, "");
  if (!/^https?:\/\//i.test(link)) link = "https://" + link;
  try {
    return new URL(link).toString();
  } catch (e) {
    return null;
  }
};

const ViewEvent = () => {
  // useLoggedIn();
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const handleClose = () => setOpen(false);

  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userRecord } = useSelector((state) => state.user);
  const prevPosts = useSelector((state) => state.prevPosts);

  const [event, setEvent] = useState(null);
  const [postState, setPostState] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [comment, setComment] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [file, setFile] = useState(null);
  const [prevFile, setPrevFile] = useState(null);
  const [commentUploading, setCommentUploading] = useState(false);
  const [commentToReply, setCommentToReply] = useState(null);
  const [commentToEdit, setCommentToEdit] = useState(null);
  const [commentToOverwrite, setCommentToOverwrite] = useState(null);

  // ── OG state for comment input ───────────────────────────────────────────────
  const [commentOgData, setCommentOgData] = useState(null);
  const [commentOgLoading, setCommentOgLoading] = useState(false);
  const [commentOgRemoved, setCommentOgRemoved] = useState(false);

  const [voteStatus, setVoteStatus] = React.useState({});
  React.useEffect(() => {
    if (!id) return;
    axios
      .get(`/event/vote-status/${id}`)
      .then((res) => {
        setVoteStatus(res.data)
      })
      .catch((err) => console.error("Failed to fetch vote status:", err));
  }, [id]);

  // ── Decode HTML entities from OG metadata (&#x27; → ', &amp; → &, etc.) ────
  const decodeHtmlEntities = (str) => {
    if (!str || typeof str !== "string") return str;
    try {
      const txt = document.createElement("textarea");
      txt.innerHTML = str;
      return txt.value;
    } catch (e) {
      return str;
    }
  };
  const decodeMetadata = (metadata) => {
    if (!metadata || typeof metadata !== "object") return metadata;
    const decoded = {};
    for (const [key, val] of Object.entries(metadata)) {
      decoded[key] = typeof val === "string" ? decodeHtmlEntities(val) : val;
    }
    return decoded;
  };

  const handleCommentOgFetched = (link) => {
    setCommentOgLoading(true);
    dispatch(
      getLinkMetadata({
        link,
        onSuccess: (metadata) => {
          const decoded = decodeMetadata(metadata);
          setCommentOgData({ sourceLink: link, ...decoded });
          setCommentOgRemoved(false);
          // If current comment is just the URL (or empty), replace with OG description + link
          try {
            const cur = (comment || "").trim();
            const isJustUrl =
              cur === "" || cur === link || cur === link.replace(/\/$/, "");
            if (decoded?.ogDescription && isJustUrl) {
              setComment(`${decoded.ogDescription}\n${link}`);
            }
          } catch (e) { }
        },
        onError: () => setCommentOgData(null),
      }),
    ).finally(() => setCommentOgLoading(false));
  };

  const handleDismissCommentOg = () => {
    setCommentOgData(null);
    setCommentOgRemoved(true);
  };

  const resetCommentOg = () => {
    setCommentOgData(null);
    setCommentOgLoading(false);
  };

  useEffect(() => {
    const getEvent = async () => {
      try {
        const res = await axios.get(`/event/${id}`);
        // Backend may return { event: {...} } or the event object directly
        const eventData = res.data?.event || res.data;
        setEvent(eventData);
      } catch (err) {
        navigate(-1);
      }
    };
    getEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!event) return;
    const postLike = {
      ...event,
      id: `event-${event.id}` || event.id,
      isEvent: true,
      user: event.organizer,
      userName: event.organizer?.userName || event.organizer?.name,
      attendees: event.attendees || [],
      media: event.media,
      groups: event.groups || [],
      communities: event.community ? [event.community] : [],
      title: event.title,
      description: event.description,
      startTime: event.startTime,
      endTime: event.endTime,
      timezone: event.timezone,
      location: event.location,
      admissionFee: event.admissionFee,
      eventType: event.eventType,
      visibility: event.privacy || event.visibility,
      // ── Link preview fields — passed through to <Post> so OpenGraphInfo renders
      postLink: event.postLink || null,
      postLinkMetadata: (() => {
        const raw = event.postLinkMetadata;
        if (!raw) return null;
        if (typeof raw === "string") {
          try {
            return JSON.parse(raw);
          } catch (e) {
            return null;
          }
        }
        return raw;
      })(),
      hideLinkPreview: Boolean(event.hideLinkPreview),
    };
    setPostState(postLike);
  }, [event]);

  const isPostGroupCommunityDisabled = (post) => {
    let isDisabled = true;
    post?.groups.forEach((group) => {
      if (group.community?.disabledAt === 0) isDisabled = false;
    });
    return isDisabled;
  };

  const isPostCommunityDisabled = (post) => {
    let isDisabled = true;
    post?.communities.forEach((com) => {
      if (com?.disabledAt === 0) isDisabled = false;
    });
    return isDisabled;
  };

  const isReadOnly = () => {
    return (
      (Boolean(postState?.groups?.length) &&
        isPostGroupCommunityDisabled(postState)) ||
      (Boolean(postState?.communities?.length) &&
        isPostCommunityDisabled(postState)) ||
      (Boolean(postState?.groups?.length) &&
        postState?.groups?.[0]?.disabledAt > 0)
    );
  };

  const getComments = async (p = page) => {
    try {
      if (!postState) return;
      setCommentsLoading(true);
      const res = await axios.get(`${GET_COMMENTS}/${postState.id}/${p}`);
      const loaded = res.data.comments || [];
      setComments((c) => (p === 1 ? loaded : [...c, ...loaded]));
      setTotalPages(res.data.totalPages);
    } catch (e) {
      // ignore
    } finally {
      setCommentsLoading(false);
    }
  };

  // ── Re-fetch OG for comments that came back from API without postLinkMetadata ─
  const rehydrateCommentOg = async (commentList) => {
    const fetchOgFor = async (item) => {
      // Already has metadata or explicitly hidden — nothing to do
      if (item.postLinkMetadata || item.hideLinkPreview) return item;
      const url = extractUrl(item.comment || "");
      if (!url) return item;
      try {
        let metadata = null;
        await dispatch(
          getLinkMetadata({
            link: url,
            onSuccess: (m) => {
              metadata = m;
            },
            onError: () => { },
          }),
        );
        if (metadata) {
          return {
            ...item,
            postLinkMetadata: { sourceLink: url, ...metadata },
          };
        }
      } catch (e) { }
      return item;
    };

    // Process comments and their replies in parallel
    return Promise.all(
      commentList.map(async (c) => {
        const [rehydratedComment, rehydratedReplies] = await Promise.all([
          fetchOgFor(c),
          Promise.all((c.replies || []).map(fetchOgFor)),
        ]);
        return { ...rehydratedComment, replies: rehydratedReplies };
      }),
    );
  };

  useEffect(() => {
    if (postState) {
      setComments([]);
      setPage(1);
      getComments(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postState]);

  const commentsDisabled = Boolean(
    postState?.status === "cancelled" || postState?.isCancelled,
  );

  const isEventPast =
    postState?.endTime && new Date(postState.endTime) < new Date();

  const getCommentRestrictionError = () => {
    if(!userRecord?.id) return "Log-in to interact";
    if (commentsDisabled) return "Activity restricted due to cancelled event";
    if (isEventPast) return "Activity restricted because the event has passed";
    return false;
  };

  useEffect(() => {
    if (postState && page > 1) getComments(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const onComment = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if(!userRecord?.id) return;
    if (!comment && !file) return;

    const formData = new FormData();
    let mediaId = "";

    if (commentToEdit?.media?.currBucketUrl) {
      mediaId = commentToEdit?.media?.id;
    } else if (commentToOverwrite?.media?.currBucketUrl) {
      mediaId = commentToOverwrite?.media?.id;
    }

    if (file) {
      setCommentUploading(true);
      formData.append("file", file);
      try {
        const up = await axios.post(UPLOAD_FILE, formData);
        if (up.data) mediaId = up.data;
        else toast.error("Something went wrong while uploading media");
      } catch (err) {
        // ignore
      } finally {
        setPrevFile(null);
        setCommentUploading(false);
      }
    }

    let newPost = prevPosts.posts?.filter((p) => p.id === postState.id)[0];
    if (!newPost && prevPosts.posts) {
      const rawId = String(postState.id).replace(/^event-/, "");
      newPost = prevPosts.posts?.filter((p) => p.id === rawId)[0];
    }
    if (newPost)
      newPost = { ...newPost, comments: (newPost.comments || 0) + 1 };

    // EDIT
    if (commentToEdit) {
      try {
        const payload = { comment, mediaId: prevFile ? mediaId : null };
        if (commentOgRemoved) {
          payload.postLinkMetadata = null;
          payload.hideLinkPreview = true;
        } else if (commentOgData) {
          payload.postLinkMetadata = commentOgData;
          payload.hideLinkPreview = false;
        }
        await axios.post(`/edit/comment/${commentToEdit.id}`, payload);
        setComments([]);
        getComments(1);
      } catch (e) {
        // ignore
      } finally {
        setCommentToEdit(null);
        setComment("");
        setPrevFile(null);
        resetCommentOg();
        setCommentOgRemoved(false);
      }

      // OVERWRITE
    } else if (commentToOverwrite) {
      try {
        const payload = {
          postId: postState.id,
          commentId: commentToOverwrite.id,
          newComment: comment,
          mediaId: prevFile ? mediaId : null,
        };
        if (commentOgRemoved) {
          payload.postLinkMetadata = null;
          payload.hideLinkPreview = true;
        } else if (commentOgData) {
          payload.postLinkMetadata = commentOgData;
          payload.hideLinkPreview = false;
        }
        await axios.post(`/group-admin-comment-overwrite`, payload);
        setComments([]);
        setPrevFile(null);
        getComments(1);
      } catch (e) {
        // ignore
      } finally {
        setCommentToOverwrite(null);
        setComment("");
        setPrevFile(null);
        resetCommentOg();
        setCommentOgRemoved(false);
      }

      // REPLY
    } else if (commentToReply) {
      const replyTo = commentToReply?.id;
      try {
        setCommentUploading(true);
        const payload = {
          userId: userRecord.id,
          postId: postState.id,
          comment,
          replyTo,
          mediaId,
          postLinkMetadata: commentOgData || null,
          hideLinkPreview: !commentOgData,
        };
        const res = await axios.post(POST_COMMENT, payload);
        await axios.post("/notification/create/reply", {
          commentId: commentToReply?.id,
        });
        const replyWithOg = {
          ...res.data,
          postLinkMetadata: commentOgData || res.data.postLinkMetadata || null,
          hideLinkPreview: !commentOgData,
        };
        const newComments = [...comments];
        const index = newComments.findIndex((c) => c.id === replyTo);
        if (index !== -1) {
          if (!newComments[index].replies) newComments[index].replies = [];
          newComments[index].replies = [
            replyWithOg,
            ...newComments[index].replies,
          ];
          setComments(newComments);
        } else {
          setComments([replyWithOg, ...comments]);
        }
        setComment("");
        resetCommentOg();
        if (newPost) dispatch(updatePost(newPost));
      } catch (err) {
        // ignore
      } finally {
        setCommentUploading(false);
        setCommentToReply(null);
      }

      // NORMAL
    } else {
      try {
        setCommentUploading(true);
        const payload = {
          userId: userRecord.id,
          postId: postState.id,
          comment,
          mediaId,
          postLinkMetadata: commentOgData || null,
          hideLinkPreview: !commentOgData,
        };
        const res = await axios.post(POST_COMMENT, payload);
        const postIdForApi =
          typeof postState.id === "string" && postState.id.startsWith("event-")
            ? postState.id
            : postState?.isEvent
              ? `event-${postState.id}`
              : postState.id;

        await axios.post("/notification/create/comment", {
          postId: postIdForApi,
        });
        setComments([
          {
            ...res.data,
            postLinkMetadata:
              commentOgData || res.data.postLinkMetadata || null,
            hideLinkPreview: !commentOgData,
          },
          ...comments,
        ]);
        setComment("");
        resetCommentOg();
        if (newPost) dispatch(updatePost(newPost));
      } catch (e) {
        toast.error("Unable to post comment");
      } finally {
        setCommentToReply(null);
        setCommentUploading(false);
      }
    }
  };

  const onCommentDelete = (id) => {
    if(!userRecord?.id) return;
    if (
      window.confirm("Are you sure you want to delete this comment?") === false
    )
      return;
    axios
      .post(`/delete/comment/${id}`)
      .then(() => {
        toast.success("Comment deleted successfully");
        setComments([]);
        getComments(1);
        let newPost = prevPosts.posts?.filter((p) => p.id === postState.id)[0];
        if (newPost) {
          newPost = { ...newPost, comments: (newPost.comments || 1) - 1 };
          dispatch(updatePost(newPost));
        }
      })
      .catch(() => { });
  };
  const location = useLocation(); // already imported via useLoggedIn or add it

  if (!event || !postState) return <Loader />;

  return (
    <Layout>
      <div
        style={{ maxHeight: "80vh" }}
        className="flex flex-col justify-between"
      >
        <div className="z-50 w-full bg-white">
          <Post
            fullTitle
            fullDescription
            post={postState}
            comment={false}
            back={true}
            setPost={setPostState}
            returnTo={location.pathname} // ✅ Post will forward this as state.returnTo when navigating to edit
            voteStatus={voteStatus}
            readOnly={!userRecord?.id}
          />
        </div>
        <div
          style={{ borderColor: theme.palette.primary.main }}
          className="px-3 flex border-t-2 flex-col justify-between"
        >
          <div
            style={{
              maxHeight: postState?.media?.currBucketUrl ? "40vh" : "60vh",
              overflowY: "scroll",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
            className="flex flex-col pb-20 pt-3"
          >
            {commentsLoading ? (
              <div className="flex justify-center py-6">
                <div className="loader" />
              </div>
            ) : (
              comments?.map((c) => (
                <Comment
                  key={c.id}
                  comment={c}
                  post={postState}
                  setCommentToReply={setCommentToReply}
                  setComment={setComment}
                  onDelete={onCommentDelete}
                  setCommentToEdit={(comment) => {
                    setCommentToEdit(comment);
                    // Seed OG state so the existing preview is preserved on save
                    const meta = comment?.postLinkMetadata;
                    if (meta) {
                      const parsed =
                        typeof meta === "string"
                          ? (() => {
                            try {
                              return JSON.parse(meta);
                            } catch (e) {
                              return null;
                            }
                          })()
                          : meta;
                      setCommentOgData(parsed || null);
                    } else {
                      setCommentOgData(null);
                    }
                    // Ensure we mark that the OG was not removed by the user when editing
                    setCommentOgRemoved(false);
                  }}
                  setPrevFile={setPrevFile}
                  setCommentToOverwrite={setCommentToOverwrite}
                />
              ))
            )}

            <div
              onClick={() => setPage((p) => p + 1)}
              className={clsx(
                totalPages <= page && "hidden",
                "grow cursor-pointer my-2 text-center mx-4 text-sm font-normal",
              )}
            />
          </div>

          {getCommentRestrictionError() ? (
            <Typography className="py-3 px-2 text-center fixed bottom-0 w-full md:w-6/12 bg-white">
              {getCommentRestrictionError()}
            </Typography>
          ) : (
            <CommentInput
              value={comment}
              setValue={setComment}
              onSubmit={onComment}
              setFile={setFile}
              prevFile={prevFile}
              setPrevFile={setPrevFile}
              loading={commentUploading}
              ogData={commentOgData}
              ogLoading={commentOgLoading}
              onOgFetched={handleCommentOgFetched}
              onDismissOg={handleDismissCommentOg}
            />
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ViewEvent;
